/* Imports */
import React, { useRef } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import '../shapes.scss';
import { dashboardActions } from '../../../../../actions/dashboard.action';
import moment from 'moment';
import parser from '../../parser';
import { findLastIndex } from 'lodash';

function toUTC(dt) {
  return moment.utc(dt, dateFormat).format();
}
/* Chart code */
// Themes begin
am4core.useTheme(am4themes_animated);
// Themes end
const dateFormat = 'MM/dd/yyyy h:mm a';

function Chart({ baseData, ...props }) {
  const [lastDataPoint, updateLastDataPoint] = React.useState([]);
  const [intervalData, updateIntervalData] = React.useState([]);
  const [init, setInit] = React.useState(false);
  const toDate = moment().format('MM/DD/yyyy HH:mm:ss');
  const fromDate = moment()
    .subtract(props.timeRange, 'minutes')
    .format('MM/DD/yyyy HH:mm:ss');

  const chart = useRef(null);
  const intervalId = useRef(null);

  React.useEffect(() => {
    let _chart = am4core.create(props.id, am4charts.XYChart);
    updateLastDataPoint(baseData[baseData.length - 1]);
    setInit(true);
    chart.current = _chart;
    return function cleanUp() {
      if (chart) {
        _chart.dispose();
      }
      if (intervalId) {
        clearInterval(intervalId.current);
      }
    };
  }, []);
  React.useEffect(() => {
    if (init) {
      drawChart();
    }
  }, [init]);
  React.useEffect(() => {
    let intervalPointIndex = findLastIndex(intervalData, [
      'date',
      lastDataPoint.date,
    ]);
    if (
      intervalPointIndex !== 0 &&
      intervalPointIndex !== intervalData.length - 1
    ) {
      let newData = intervalData.slice(intervalPointIndex + 1);
      if (newData.length) {
        chart.current.addData(newData);

        updateLastDataPoint((dtPoint) => {
          dtPoint = newData[newData.length - 1];
          return dtPoint;
        });
      }
    }
  }, [intervalData]);

  function drawChart() {
    chart.current.dateFormatter.dateFormat = dateFormat;
    chart.current.hiddenState.properties.opacity = 0;
    chart.current.background.fill = props.backgroundColor;
    chart.current.padding(0, 0, 0, 0);

    chart.current.zoomOutButton.disabled = true;

    chart.current.legend = new am4charts.Legend();
    chart.current.cursor = new am4charts.XYCursor();
    chart.current.cursor.maxTooltipDistance = 0;
    chart.current.cursor.behavior = 'none';

    chart.current.data = baseData;

    let dateAxis = chart.current.xAxes.push(new am4charts.DateAxis());
    // dateAxis.groupData = true;
    dateAxis.renderer.grid.template.location = 0;
    dateAxis.renderer.minGridDistance = 25;
    dateAxis.dateFormats.setKey('day', dateFormat);
    dateAxis.periodChangeDateFormats.setKey('second', '[bold]h:mm a');
    dateAxis.periodChangeDateFormats.setKey('minute', `[bold]${dateFormat}`);
    dateAxis.periodChangeDateFormats.setKey('hour', `[bold]${dateFormat}`);
    dateAxis.periodChangeDateFormats.setKey('day', `[bold]${dateFormat}`);
    dateAxis.renderer.inside = true;
    dateAxis.renderer.axisFills.template.disabled = true;
    dateAxis.renderer.ticks.template.disabled = true;

    let valueAxis = chart.current.yAxes.push(new am4charts.ValueAxis());
    valueAxis.tooltip.disabled = true;
    valueAxis.interpolationDuration = 500;
    valueAxis.rangeChangeDuration = 500;
    valueAxis.renderer.inside = true;
    valueAxis.renderer.minLabelPosition = 0.05;
    valueAxis.renderer.maxLabelPosition = 0.95;
    valueAxis.renderer.axisFills.template.disabled = true;
    valueAxis.renderer.ticks.template.disabled = true;

    let series = chart.current.series.push(new am4charts.LineSeries());
    series.dataFields.dateX = 'date';
    series.dataFields.valueY = props.sensorName;
    series.interpolationDuration = 500;
    series.defaultState.transitionDuration = 0;
    series.tensionX = 0.8;

    series.name = props.sensorName;
    series.tooltipText =
      '{dateX.formatDate("MM/dd/yyyy h:mm a")}: [b]{valueY}[/]';
    series.strokeWidth = 2;

    // Set up tooltip
    series.adapter.add('tooltipText', (ev) => {
      let text = '[bold]{dateX.formatDate("MM/dd/yyyy h:mm a")}[/]\n';
      chart.current.series.each(function (item) {
        text +=
          '[' +
          item.stroke.hex +
          ']●[/] ' +
          item.name +
          ': {' +
          item.dataFields.valueY +
          '}' +
          ' ' +
          props.units +
          '\n';
      });
      return text;
    });

    series.tooltip.getFillFromObject = false;
    series.tooltip.background.fill = am4core.color('#fff');
    series.tooltip.label.fill = am4core.color('#00');
    series.smoothing = 'monotoneX';

    chart.current.events.on('datavalidated', function () {
      dateAxis.zoom({ start: 1 / 15, end: 1.2 }, false, true);
    });

    dateAxis.interpolationDuration = 500;
    dateAxis.rangeChangeDuration = 500;

    // add data
    const _intervalId = setInterval(async () => {
      let intervalPointIndex;
      let newData = [];
      const toDate = toUTC(moment().toDate());
      const fromDate = toUTC(
        moment().subtract(props.timeRange, 'minutes').toDate(),
      );
      await dashboardActions
        .getTrendV2(props.sensorId, fromDate, toDate)
        .then((resp) => {
          const intervalData = parser(resp)[1];
          if (intervalData.length) {
            updateIntervalData(intervalData);
          }
        })
        .catch((err) => console.log(err));
    }, props.refreshRate);
    intervalId.current = _intervalId;

    // all the below is optional, makes some fancy effects
    // gradient fill of the series
    series.fillOpacity = 1;
    let gradient = new am4core.LinearGradient();
    gradient.addColor(chart.current.colors.getIndex(0), 0.2);
    gradient.addColor(chart.current.colors.getIndex(0), 0);
    series.fill = gradient;

    // this makes date axis labels to fade out
    dateAxis.renderer.labels.template.adapter.add(
      'fillOpacity',
      function (fillOpacity, target) {
        let dataItem = target.dataItem;
        return dataItem.position;
      },
    );

    // need to set this, otherwise fillOpacity is not changed and not set
    dateAxis.events.on('validated', function () {
      am4core.iter.each(dateAxis.renderer.labels.iterator(), function (label) {
        //eslint-disable-next-line
        label.fillOpacity = label.fillOpacity;
      });
    });

    // this makes date axis labels which are at equal minutes to be rotated
    dateAxis.renderer.labels.template.adapter.add(
      'rotation',
      function (rotation, target) {
        let dataItem = target.dataItem;
        if (
          dataItem.date &&
          //eslint-disable-next-line
          dataItem.date.getTime() ==
            am4core.time
              .round(new Date(dataItem.date.getTime()), 'minute')
              .getTime()
        ) {
          target.verticalCenter = 'middle';
          target.horizontalCenter = 'left';
          return -90;
        } else {
          target.verticalCenter = 'bottom';
          target.horizontalCenter = 'middle';
          return 0;
        }
      },
    );

    // const bullet = series.bullets.push(new am4charts.CircleBullet());
    // bullet.circle.stroke = am4core.color('#fff');
    // bullet.circle.strokeWidth = 2;
    // bullet at the front of the line
    let bullet = series.createChild(am4charts.CircleBullet);
    bullet.circle.radius = 2;
    bullet.fillOpacity = 1;
    bullet.fill = chart.current.colors.getIndex(0);
    // bullet.isMeasured = false;

    series.events.on('validated', function () {
      bullet.moveTo(series.dataItems.last.point);
      bullet.validatePosition();
    });
  }

  return (
    <div
      id={props.id}
      className="map-viewer-chart"
      style={{
        width: '100%',
        height: '100%',
        // top: props.y,
        // left: props.x,
        // backgroundColor: props.fill,
        // position: 'absolute',
        // borderWidth: props.borderSize,
        // borderStyle: props.borderStyle,
        // borderColor: props.borderColor,
        // zIndex: (props.index || 0) + 1,
      }}
    ></div>
  );
}

export default Chart;
